import React from "react";
import permissionsImg from "../assets/permissions-img.png";
import cameraBlockedImg from "../assets/camera_blocked.png";
import micBlockedImg from "../assets/mic_blocked.png";
import lightbulbIcon from "../assets/lightbulb_orange.svg";

interface PermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestPermissions: () => void;
  permissionStatus: "notEnabled" | "permanentlyBlocked";
  cameraBlocked: boolean;
  micBlocked: boolean;
  windowManagementBlocked?: boolean;
}

const PermissionModal: React.FC<PermissionModalProps> = ({
  isOpen,
  onClose,
  onRequestPermissions,
  permissionStatus,
  cameraBlocked,
  micBlocked,
  windowManagementBlocked = false,
}) => {
  if (!isOpen) return null;

  const isBlocked =
    permissionStatus === "permanentlyBlocked" || cameraBlocked || micBlocked || windowManagementBlocked;

  const getBlockedHeading = () => {
    if (windowManagementBlocked) {
      if (cameraBlocked && micBlocked) return "Permissions Blocked";
      return "Screen Permission Blocked";
    }
    if (cameraBlocked && micBlocked) return "Camera & Microphone Blocked";
    if (cameraBlocked) return "Camera Blocked";
    if (micBlocked) return "Microphone Blocked";
    return "Permissions Required";
  };

  const getImageSource = () => {
    if (windowManagementBlocked) return permissionsImg;
    if (cameraBlocked && micBlocked) return permissionsImg;
    if (cameraBlocked) return cameraBlockedImg;
    if (micBlocked) return micBlockedImg;
    return permissionsImg;
  };

  const getPermissionMessage = () => {
    if (windowManagementBlocked && cameraBlocked && micBlocked) {
      return "Enable window management along with camera and microphone access in your browser settings.";
    }
    if (windowManagementBlocked) {
      return "Enable window management permission in your browser settings to continue.";
    }
    if (cameraBlocked && micBlocked) return "Enable access to your camera and microphone.";
    if (cameraBlocked) return "Enable access to your camera.";
    if (micBlocked) return "Enable access to your microphone.";
    return "Enable access to your microphone and camera.";
  };

  const getButtonText = () => {
    if (isBlocked) return "Try Again";
    return "Request Permissions";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <button
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-800 transition-colors"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
            <path
              d="M6 18L18 6M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex items-center justify-center px-10 pt-11 bg-gradient-to-b from-[#D5D5FF] to-[#C8C8F0]">
          <img src={getImageSource()} alt="permissions" />
        </div>

        <div className="text-body space-y-[32px] p-8">
          <div className="space-y-[20px]">
            <div>
              <h2 className="text-grey-7 font-ubuntu text-[24px] font-medium leading-[38.4px]">
                {isBlocked ? getBlockedHeading() : "Allow Permissions to Proceed"}
              </h2>
            </div>

            <div className="space-y-2">
              <p className="text-grey-7 font-ubuntu text-[16px] font-normal leading-[24px]">To begin your interview</p>
              <p className="text-grey-7 font-ubuntu text-[16px] font-normal leading-[24px]">
                {getPermissionMessage()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[16px] font-normal leading-[21px] text-[#00143D] bg-[#FFF6E5] rounded-lg p-2">
            <span>
              <img src={lightbulbIcon} alt="lightbulb" className="w-5 h-5" />
            </span>
            Without this, we can't start the session.
          </div>

          <div className="flex items-center justify-center">
            <button
              className="bg-button text-white px-4 py-2 rounded w-full"
              onClick={onRequestPermissions}
              type="button"
            >
              {getButtonText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;
